import React, { useState } from "react";
import Select, { components } from "react-select";
import { SELECT_ALL } from "./constants";

// react-select pieces shared by the report pages.

export const SELECT_ALL_OPTION = { value: SELECT_ALL, label: "Select All" };

export const selectStyles = (width) => ({
  container: (base) => ({ ...base, width }),
  control: (base, state) => ({
    ...base,
    minHeight: "40px",
    borderRadius: "10px",
    borderColor: state.isFocused ? "#2050FF" : "#E9ECEF",
    boxShadow: "none",
    fontSize: "14px",
    ":hover": { borderColor: state.isFocused ? "#2050FF" : "#C7D0E0" },
  }),
  // Multi-selects stay one row tall instead of growing with every chip.
  valueContainer: (base) => ({ ...base, maxHeight: "72px", overflowY: "auto" }),
  placeholder: (base) => ({ ...base, color: "#6C757D" }),
  menu: (base) => ({ ...base, borderRadius: "12px", overflow: "hidden", zIndex: 20 }),
  indicatorSeparator: () => ({ display: "none" }),
});

// A multi-select whose first option toggles everything on or off. Picking
// "Select All" while everything is already picked clears the selection.
// Picked values that are not among the options (device IDs typed in by hand)
// are left as they are either way.
export const withSelectAll = (picked, allOptions) => {
  if (picked?.some((option) => option.value === SELECT_ALL)) {
    const known = new Set(allOptions.map((option) => option.value));
    const extras = picked.filter(
      (option) => option.value !== SELECT_ALL && !known.has(option.value)
    );
    const everythingPicked = picked.length - 1 - extras.length === allOptions.length;
    return everythingPicked ? extras : [...allOptions, ...extras];
  }
  return picked || [];
};

// True when every option is picked. Checked by value, so typed-in extras
// (device IDs not among the options) do not make a partial pick read as all.
const allPicked = (picked, options) => {
  if (options.length === 0) return false;
  const values = new Set(picked.map((option) => option.value));
  return options.every((option) => values.has(option.value));
};

// What a multi-select shows in place of its chips, so the box stays one line
// tall however many are picked. A single pick reads as `labelOf` gives it (the
// select's formatOptionLabel, so a store shows its code too).
export const summaryOf = (picked, options, { empty, nouns }, labelOf = (option) => option.label) => {
  if (picked.length === 0) return empty;
  if (picked.length === 1) return labelOf(picked[0]);
  if (picked.length === options.length && allPicked(picked, options)) return `All ${nouns}`;
  return `${picked.length} ${nouns}`;
};

// A store option's text, in the menu and as the box's single pick: the store
// code first, then the name, as everywhere a store is named with its code
// ("DPI70308 - Ajmal Khan Road"). Rows that are not stores (Select All, typed
// values) show their label alone.
export const storeOptionLabel = (option) =>
  option.property && option.value !== option.label
    ? `${option.value} - ${option.label}`
    : option.label;

// A menu row with a checkbox, since the box no longer shows the picks.
// "Select All" reads as checked once everything is picked.
function CheckOption(props) {
  const { data, isSelected, selectProps } = props;
  const checked =
    data.value === SELECT_ALL
      ? allPicked(selectProps.value || [], selectProps.options.slice(1))
      : isSelected;
  return (
    <components.Option {...props}>
      <span className="rp-option">
        <input type="checkbox" checked={checked} readOnly tabIndex={-1} />
        <span>{props.children}</span>
      </span>
    </components.Option>
  );
}

// `options` with the picked ones moved to the top, each group in its own order.
export const pickedFirst = (options, pickedValues) => [
  ...options.filter((option) => pickedValues.has(option.value)),
  ...options.filter((option) => !pickedValues.has(option.value)),
];

// A multi-select with a Select All row, checkboxes, and a one-line summary.
// What is picked is listed first. The order is taken when the menu opens and
// held while it is open, so a row does not jump away from the pointer as it is
// ticked. `SelectComponent` swaps in react-select's CreatableSelect where typed
// values are allowed.
export function MultiSelect({
  options,
  value,
  onChange,
  summary,
  width,
  SelectComponent = Select,
  onMenuOpen,
  ...rest
}) {
  const base = selectStyles(width);
  // The values picked when the menu last opened.
  const [pickedAtOpen, setPickedAtOpen] = useState(() => new Set());
  const ordered = pickedFirst(options, pickedAtOpen);
  return (
    <SelectComponent
      isMulti
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      controlShouldRenderValue={false}
      components={{ Option: CheckOption }}
      options={[SELECT_ALL_OPTION, ...ordered]}
      value={value}
      onMenuOpen={() => {
        setPickedAtOpen(new Set(value.map((option) => option.value)));
        onMenuOpen?.();
      }}
      onChange={(picked) => onChange(withSelectAll(picked, options))}
      placeholder={summaryOf(
        value,
        options,
        summary,
        rest.formatOptionLabel && ((option) => rest.formatOptionLabel(option, { context: "value" }))
      )}
      styles={{
        ...base,
        // The summary stands in for the value, so it reads as one.
        placeholder: (style) => ({
          ...base.placeholder(style),
          ...(value.length > 0 && { color: "#212529" }),
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }),
        option: (style, state) => ({
          ...style,
          fontSize: "14px",
          color: "#212529",
          backgroundColor: state.isFocused ? "#F5F7FF" : "#FFFFFF",
          cursor: "pointer",
        }),
      }}
      {...rest}
    />
  );
}

// Compact react-select boxes for inside table cells. The menu is portalled to
// the body so the table's scroll container does not clip it.
export const cellSelectStyles = (width) => ({
  ...selectStyles(width),
  control: (base, state) => ({
    ...base,
    minHeight: "34px",
    height: "34px",
    borderRadius: "8px",
    borderColor: state.isFocused ? "#2050FF" : "#E9ECEF",
    boxShadow: "none",
    fontSize: "13.5px",
    backgroundColor: "#FFFFFF",
    ":hover": { borderColor: state.isFocused ? "#2050FF" : "#C7D0E0" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  indicatorsContainer: (base) => ({ ...base, height: "32px" }),
  dropdownIndicator: (base) => ({ ...base, padding: "4px" }),
  clearIndicator: (base) => ({ ...base, padding: "4px" }),
  menuPortal: (base) => ({ ...base, zIndex: 1500 }),
  menu: (base) => ({ ...base, borderRadius: "10px", overflow: "hidden", fontSize: "13.5px" }),
  option: (base, state) => ({
    ...base,
    color: "#212529",
    backgroundColor: state.isSelected ? "#EDF1FF" : state.isFocused ? "#F5F7FF" : "#FFFFFF",
    fontWeight: state.isSelected ? 600 : 400,
    cursor: "pointer",
  }),
});
